import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useHasContactJoined } from "@hooks/use-chat";
import useClientAssignee from "@hooks/use-client-assignee";
import { displayNameFromContact } from "@lib/contacts";
import { contactForm } from "@lib/utils/contacts";

import { ErrorMessage } from "@components/ErrorMessage";
import ChatIcon from "@components/Icons/ChatIcon";
import SmileIcon from "@components/Icons/SmileIcon";

import ClientContainerLayout from "./Client/ClientContainerLayout";
import ClientFooterLayout from "./Client/ClientFooterLayout";
import ClientLayout from "./Client/ClientLayout";
import SubmitButton, { VARIANTS } from "./Client/SubmitButton";
import { IntakeFormFields } from "./IntakeForm/IntakeFormFields";
import { Button } from "./Button";
import { ContactInfoForm } from "./ContactInfoForm";
import EmptyList from "./EmptyList";

interface ClientFormProps {
  form: any;
  coach: any;
  loading: boolean;
  onSubmit: (data: any, e: any) => void;
  requireContactInfo?: boolean;
  canReSubmit?: boolean;
  showSubmitButton?: boolean;
  submitTitle?: string;
  messagingInvite?: boolean;
  contactId?: string;
  contact?: any;
  currencies?: any;
  beforeTitle?: any;
  interceptSubmit?: (e: any, handleSubmit: any, onSubmit: any) => void;
  onSubmitSuccessfulChange?: (isSubmitSuccessful: boolean) => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  form,
  coach,
  loading,
  onSubmit,
  requireContactInfo,
  canReSubmit = true,
  showSubmitButton = true,
  submitTitle = "Submit",
  messagingInvite,
  contactId,
  contact = undefined,
  currencies = undefined,
  beforeTitle = undefined,
  interceptSubmit = undefined,
  onSubmitSuccessfulChange = undefined,
}) => {
  const { clientAssignee } = useClientAssignee(contactId || "", coach?.id);
  const clientAssigneeName = displayNameFromContact(
    clientAssignee ? clientAssignee : coach,
    true
  );

  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      ...form,
      ...contactForm(contact),
    },
  });
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    setValue,
    control,
    watch,
  } = methods;
  const hasContactJoined = useHasContactJoined(coach?.id, contactId);

  // Call the callback when isSubmitSuccessful changes
  useEffect(() => {
    if (onSubmitSuccessfulChange) {
      onSubmitSuccessfulChange(isSubmitSuccessful);
    }
  }, [isSubmitSuccessful, onSubmitSuccessfulChange]);

  const containsLegalQuestion = !!form?.items?.find(
    (q) => q.questionType === "legal"
  );
  const successMessage = containsLegalQuestion
    ? "Thank you!"
    : canReSubmit
      ? "Thank you! Feel free to update your answers anytime."
      : `Thank you! Your answers have been submitted to ${clientAssigneeName}.`;

  const readonly =
    containsLegalQuestion && form?.status === "submitted" && !isSubmitting;

  const signerContact = contact
    ? contact
    : {
        firstName: watch("firstName"),
        lastName: watch("lastName"),
        email: watch("email"),
      };

  return (
    <FormProvider {...methods}>
      <ClientLayout>
        <ClientContainerLayout
          icon={form?.icon}
          iconFileType="svg"
          title={form?.title}
          coach={coach}
          isLoading={loading}
          beforeTitle={beforeTitle}
        >
          <>
            <div className="w-full sm:grid grid-cols-12 mb-24 mt-4">
              <div className="sm:col-start-2 sm:col-span-10">
                <form data-name="Booking Form">
                  {!isSubmitSuccessful && form && (
                    <>
                      {form?.description && (
                        <div className="text-foreground/75 mb-8 md:mb-12 whitespace-pre-wrap">
                          {form.description}
                        </div>
                      )}
                      {requireContactInfo ? (
                        <ContactInfoForm register={register} errors={errors} />
                      ) : (
                        <div className="font-sm mb-8 text-foreground/50">
                          * Required information
                        </div>
                      )}
                      {form && (
                        <IntakeFormFields
                          readonly={readonly}
                          form={form}
                          setValue={setValue}
                          register={register}
                          control={control}
                          errors={errors}
                          coachId={coach?.id}
                          contact={signerContact}
                          currencies={currencies}
                        />
                      )}
                    </>
                  )}

                  <div className="mt-8 text-center justify-center">
                    {isSubmitSuccessful ? (
                      <>
                        <div className="sm:col-start-4 sm:col-span-6 bg-accent/7 text-foreground rounded-lg px-4 py-8 flex flex-col items-center text-center">
                          <SmileIcon className="w-8 h-8 text-accent/50" />
                          <h1 className="text-xl sm:text-xl font-medium leading-tight mt-4">
                            Form submitted!
                          </h1>
                          <p className="mt-4">{successMessage}</p>
                          {canReSubmit && !readonly && (
                            <Button
                              onClick={() => window.location.reload()}
                              white
                              className="w-full cursor-pointer mt-8"
                            >
                              Edit Form
                            </Button>
                          )}
                        </div>

                        {(messagingInvite || hasContactJoined) && (
                          <div className="sm:col-start-4 sm:col-span-6 bg-foreground/7 rounded-lg mt-2 flex flex-col items-center text-center">
                            <EmptyList
                              className="w-full"
                              variant="transparent"
                              icon={<ChatIcon className="text-foreground" />}
                              description={
                                <div className="text-left text-foreground">
                                  <strong className="font-medium ">
                                    {hasContactJoined
                                      ? "Open client portal"
                                      : `Want to message ${clientAssigneeName} directly?`}
                                  </strong>
                                  {!hasContactJoined && (
                                    <div>Start your private channel</div>
                                  )}
                                </div>
                              }
                              buttonAction={() =>
                                window.open(
                                  `/users/${
                                    coach.id
                                  }/clients/${encodeURIComponent(
                                    contactId || ""
                                  )}/messaging`
                                )
                              }
                              Button={(props) => (
                                <Button
                                  white
                                  small
                                  className={VARIANTS.secondary}
                                  {...props}
                                >
                                  Open
                                </Button>
                              )}
                            />
                          </div>
                        )}
                      </>
                    ) : null}
                    {!isSubmitSuccessful && !readonly && form && (
                      <div className="mt-4 flex flex-col">
                        {errors?.answers?.length >= 1 && (
                          <ErrorMessage className="mb-1 items-center">
                            Please fill out all required fields
                          </ErrorMessage>
                        )}
                        {showSubmitButton && (
                          <SubmitButton
                            actionTitle={submitTitle}
                            onAction={(e) => {
                              if (interceptSubmit) {
                                interceptSubmit(e, handleSubmit, onSubmit);
                              } else {
                                handleSubmit(onSubmit)(e);
                              }
                            }}
                            errors={errors}
                            className="self-center"
                            isLoading={isSubmitting}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </>
        </ClientContainerLayout>
        <ClientFooterLayout coach={coach} />
      </ClientLayout>
    </FormProvider>
  );
};
